import { Inject, Injectable, Optional } from '@angular/core';

import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  publishReplay,
  refCount,
  switchMap,
} from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';
import { getIntersection } from '@recruitee/utils';

import { BulkActionsController } from './bulk-actions.controller';
import {
  BULK_SELECTION_TRACKER_ITEM_FILTER,
  BulkItemBase,
  BulkSelectionTrackerItemFilter,
} from './bulk-actions.types';

@Injectable()
export class BulkActionsSelectionTracker {
  public state$: Observable<TrackerState>;
  public _items$: BehaviorSubject<BulkItemBase[]> = new BehaviorSubject<BulkItemBase[]>([]);
  public items$ = this._items$.pipe(
    switchMap(items => (this.itemFilter ? this.itemFilter(items) : of(items))),
  );

  public set items(items: BulkItemBase[]) {
    this._items$.next(items);
  }

  constructor(
    private bulkCtrl: BulkActionsController,
    private store: Store,
    @Optional()
    @Inject(BULK_SELECTION_TRACKER_ITEM_FILTER)
    private itemFilter?: BulkSelectionTrackerItemFilter,
  ) {
    this.state$ = this.bulkCtrl.selectedItems$.asObservable().pipe(
      filterBoolean,
      combineLatest(this.items$.pipe(filter(items => !!items)), (selectedItems, allItems) => {
        const totalSelectedCount = selectedItems.length;

        const currentViewSelectedCount = getIntersection(
          selectedItems.map(i => i.id),
          allItems.map(i => i.id),
        ).length;

        const shouldDisplayMessage = totalSelectedCount > currentViewSelectedCount;
        return {
          totalSelectedCount,
          currentViewSelectedCount,
          currentViewTotalCount: allItems.length,
          shouldDisplayMessage,
        };
      }),
      distinctUntilChanged(deepEqual),
      publishReplay(),
      refCount(),
    );
  }
}

type TrackerState = {
  totalSelectedCount: number;
  currentViewSelectedCount: number;
  currentViewTotalCount: number;
  shouldDisplayMessage: boolean;
};
